import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDataActivity } from '../../features/Activity/Activity';
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SeeMore } from '../../Buttons/Buttons';
import getDirctionLang from '../../Actions/getDirctionLang';
import Loading from '../Loading';

// Swiper for drag & drop support
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/autoplay';

function ActivityPage() {
    const dispatch = useDispatch();
    const { i18n, t } = useTranslation();

    useEffect(() => {
        dispatch(fetchDataActivity({ perPage: 8 }));
    }, [dispatch, i18n.language]);

    const { activities, loading } = useSelector((state) => state.activity);
    const isRtl = getDirctionLang();

    if (loading) return <Loading />;

    return (
        <div
            className="w-full py-8 md:py-12 bg-transparent text-gray-900 dark:text-white my-4 md:my-10 relative transition-colors duration-300 overflow-hidden"
            dir={isRtl ? "rtl" : "ltr"}
        >
            {/* Linear continuous scroll style for Swiper */}
            <style>{`
                .mySwiper .swiper-wrapper {
                    transition-timing-function: linear !important;
                }
            `}</style>

            <motion.div 
                initial={{ opacity: 0, x: isRtl ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } }}
                viewport={{ once: true, margin: "-100px" }}
                className="container mx-auto px-4 md:px-8 mb-8 flex justify-between items-center relative z-20"
            >
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tighter uppercase text-gray-900 dark:text-white">
                    {t("latestNews") || "Latest Activity"}
                </h2>
                <div className="shrink-0">
                    <SeeMore link={"/activity"} />
                </div>
            </motion.div>

            {activities.length > 0 && (
                <motion.div 
                    initial={{ opacity: 0, y: 100 }}
                    whileInView={{ opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut", delay: 0.15 } }}
                    viewport={{ once: true, margin: "-100px" }}
                    className="relative w-full py-4"
                >
                    {/* Swiper replaces pure CSS so user can click and drag. 
                        Using mask-image for seamless edge fades instead of solid color gradients! */}
                    <Swiper
                        key={i18n.language}
                        modules={[Autoplay]}
                        spaceBetween={24}
                        slidesPerView="auto"
                        loop={activities?.length >= 3}
                        speed={5000} // Smooth slow speed
                        grabCursor={true}
                        autoplay={{
                            delay: 0, // Continuous scrolling
                            disableOnInteraction: false,
                            pauseOnMouseEnter: true,
                        }}
                        dir={isRtl ? "rtl" : "ltr"}
                        className="mySwiper w-full px-4 md:px-16 !overflow-visible"
                        style={{
                            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
                            maskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)'
                        }}
                    >
                        {activities.map((activity, index) => (
                            <SwiperSlide key={`${activity.id}-${index}`} className="!w-[260px] sm:!w-[300px] md:!w-[360px]">
                                <Link 
                                  to={`/activity/${activity.id}`} 
                                  className="group relative w-full aspect-[4/5] overflow-hidden bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800/50 cursor-pointer shadow-[0_8px_30px_rgb(0,0,0,0.12)] dark:shadow-2xl block rounded-xl md:rounded-2xl"
                                >
                                    <img
                                        src={process.env.REACT_APP_IMG_URL + activity.image}
                                        alt={activity.title}
                                        className="w-full h-full object-cover grayscale-[20%] contrast-125 group-hover:grayscale-0 group-hover:scale-110 transition-transform duration-700 ease-out"
                                    />
                                    
                                    {/* Green Overlays (Changed from Crimson Red) */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-black/10 opacity-90 group-hover:opacity-70 transition-opacity duration-500 pointer-events-none"></div>
                                    <div className="absolute inset-0 bg-green-900/20 mix-blend-overlay group-hover:bg-green-600/20 transition-colors duration-500 pointer-events-none"></div>

                                    {/* Text Content */}
                                    <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8 z-10">
                                        <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500 ease-out">
                                            {/* Date has been completely removed as requested */}
                                            
                                            <h3 className="text-xl md:text-2xl font-bold text-white leading-snug drop-shadow-lg mb-2">
                                                {activity.title}
                                            </h3>
                                            <p 
                                                className="text-gray-300 text-sm md:text-base line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-150"
                                                dangerouslySetInnerHTML={{ 
                                                    __html: activity.description.length > 80 
                                                        ? activity.description.substring(0, 80) + "..." 
                                                        : activity.description 
                                                }}
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Sharp Edge Border (Green) */}
                                    <div className="absolute inset-0 border-2 border-transparent group-hover:border-green-600/60 transition-colors duration-500 z-20 pointer-events-none rounded-md"></div>
                                </Link>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </motion.div>
            )}
        </div>
    );
}

export default ActivityPage;